import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import {
  IPaymentDebitNoteApproveReject,
  usePaymentDebitNoteApproveRejectAPI,
} from "@/app/hooks/api_hooks/payment/usePaymentDebitNoteApproveRejectAPI";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { useSearchParams } from "react-router-dom";
import { usePaymentDebitNoteListAPI } from "@/app/hooks/api_hooks/payment/usePaymentDebitNoteListAPI";
import { usePaymentFinanceDebitNoteListAPI } from "@/app/hooks/api_hooks/payment/usePaymentFinanceDebitNoteListAPI";
import { useForm } from "react-hook-form";
import { usePaymentUserWalletAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletAPI";

const DebitNoteApproveModal = ({ userId, open, setOpen, debitNoteReceiptId, approvalStatus }) => {
  const cancelButtonRef = useRef(null);
  const { getRouteKey } = useRouter();
  const [params, _setParams] = useSearchParams();
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { debitNoteFilter } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;

  const { execute: debitNoteApproveRejectAPI } = usePaymentDebitNoteApproveRejectAPI();
  const { execute: fetchDebitNote } = usePaymentDebitNoteListAPI();
  const { execute: fetchUserWalletDetails } = usePaymentUserWalletAPI();
  const { execute: fetchDebitNoteFinanceList } = usePaymentFinanceDebitNoteListAPI();

  const isCustomerLabel = params.get("from_cart");

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      refund_type: "wallet",
    },
  });

  const [refComments, setRefComments] = useState("");

  useEffect(() => {
    if (debitNoteFilter) {
      formHook.setValue("filters", debitNoteFilter);
    }
  }, [debitNoteFilter]);

  useEffect(() => {
    if (!open) {
      setRefComments("");
    }
  }, [open]);

  const handelSubmit = () => {
    const constructedBody: IPaymentDebitNoteApproveReject = {
      debit_note_receipt_id: debitNoteReceiptId,
      status: approvalStatus === "Approve" ? "approved" : "rejected",
      user_id: isCustomerLabel ? customerId : userId,
      ...(approvalStatus === "Approve" ? { approve_ref_comment: refComments } : {}),
    };
    debitNoteApproveRejectAPI(constructedBody).then(({ status }) => {
      fetchUserWalletDetails(customerId);
      if (status) {
        setOpen(false);
        if (isCustomerLabel) {
          fetchDebitNote(customerId, debitNoteFilter?.startdate, debitNoteFilter?.enddate, debitNoteFilter?.status);
        } else {
          const filter = formHook.getValues("filters");
          fetchDebitNoteFinanceList(
            filter?.startdate,
            filter?.enddate,
            filter?.user ? debitNoteFilter?.user : "",
            filter?.status,
          );
        }
      }
    });
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-3xl">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mt-0 border-b pb-2">
                    <h2 className="text-sm font-medium">Are you sure you want to {approvalStatus} the Debit Note</h2>

                    <span
                      onClick={() => {
                        setOpen(false);
                      }}
                    >
                      <XMarkIcon className="h-4 w-4 cursor-pointer" />
                    </span>
                  </div>

                  {approvalStatus === "Approve" && (
                    <div className="mt-4">
                      <label htmlFor="approval_ref_comments" className="block text-xs font-semibold text-[#1F1F23] mb-1">
                        Bank Reference Number/Comments
                      </label>
                      <input
                        type="text"
                        id="approval_ref_comments"
                        name="approval_ref_comments"
                        className="mt-4 block w-80 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-xs sm:leading-6"
                        placeholder="Enter bank reference number/comments"
                        value={refComments}
                        onChange={(e) => setRefComments(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="flex justify-end gap-4 mt-4">
                    <button
                      type="submit"
                      onClick={() => {
                        setOpen(false);
                      }}
                      className="rounded-md bg-[#72767f1a] px-4 py-2 text-sm text-[#000000] font-medium "
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-[var(--color-primary)] px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg text-sm text-white "
                      onClick={() => {
                        handelSubmit();
                      }}
                    >
                      Yes, {approvalStatus}
                    </button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default DebitNoteApproveModal;
